import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { NotaFiscalEntradaAssociacaoDialogComponent } from '@shared/components/nota-fiscal-entrada-associacao-dialog/nota-fiscal-entrada-associacao-dialog.component';
import {
  NotaFiscalEntradaAssociacaoDialogData,
  NotaFiscalEntradaAssociacaoDialogResult,
} from '@shared/components/nota-fiscal-entrada-associacao-dialog/nota-fiscal-entrada-associacao-dialog-data';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { OrigemAssociacaoItensNFeEntradaEnum } from '@shared/enums/suprimentos/origem-associacao-itens-nfe-entrada.enum';
import {
  classeBadgeStatusNFeEntrada,
  iconeStatusNFeEntrada,
  rotuloStatusNFeEntrada,
} from '@shared/helpers/nota-fiscal-entrada-status.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { CpfCnpjPipe } from '@shared/pipes/cpf-cnpj.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '@shared/services/requests/pessoa/list-pessoa-ids-request';
import { NotaFiscalEntradaGavetaListarRequest } from '@shared/services/requests/orquestrador-compras/nota-fiscal-entrada-gaveta-listar.request';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { CompraVinculadaNFeStruct } from '@shared/services/structs/compras/compra-vinculada-nfe.struct';
import { NotaFiscalEntradaGavetaStruct } from '@shared/services/structs/orquestrador-compras/nota-fiscal-entrada-gaveta.struct';
import {
  AvisoAssociacaoPendenteDialogData,
  AvisoAssociacaoPendenteDialogResult,
} from '../../dialogs/aviso-associacao-pendente-dialog/aviso-associacao-pendente-dialog.data';
import { AvisoAssociacaoPendenteDialogComponent } from '../../dialogs/aviso-associacao-pendente-dialog/aviso-associacao-pendente-dialog.component';
import { VinculoPedidoNfDialogData, VinculoPedidoNfDialogResult } from '../../dialogs/vinculo-pedido-nf-dialog/vinculo-pedido-nf-dialog.data';
import { VinculoPedidoNfDialogComponent } from '../../dialogs/vinculo-pedido-nf-dialog/vinculo-pedido-nf-dialog.component';
import { isItemAssociacaoPendente } from '../../processo-compras.associacao';

interface FornecedorResumo {
  nome: string;
  cnpj: string | null;
}

/** Preferência local: o aviso azul da gaveta fica oculto até o usuário pedir para vê-lo de novo. */
const AVISO_OCULTO_STORAGE_KEY = 'ATS_GAVETA_NFS_AVISO_OCULTO';

/**
 * Gaveta de NFs — painel lateral do Kanban de Compras com as notas de entrada que ainda não têm
 * pedido vinculado. O card tem um botão só, pelas divergências gravadas na última conferência da nota
 * (a API decide `possuiDivergenciaBloqueiaVinculo` e `possuiAssociacaoPendente` pelos tipos gravados):
 * - divergência que impede o vínculo: "Resolver divergências" (secundário), que leva ao cadastro da
 *   nota na Central;
 * - senão, associação pendente: "Associar itens" (primário) - a associação é a única divergência
 *   tratada pela própria gaveta;
 * - senão: "Vincular ao pedido" (primário), que abre direto o modal do vínculo - ele sugere o pedido
 *   pelo `xPed` do XML ou deixa o usuário buscar.
 * Arrastar a nota sobre o card do Kanban e o pedido sugerido na gaveta continuam fora desta entrega.
 *
 * O componente fica sempre instanciado para manter o contador do botão do cabeçalho vivo mesmo com
 * o painel fechado; só o painel em si é que aparece e some com o `aberta`.
 */
@Component({
  selector: 'app-gaveta-nfs',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
    SelectPesquisavelComponent,
    AbntPipe,
    CpfCnpjPipe,
    DatePipe,
  ],
  templateUrl: './gaveta-nfs.component.html',
  styleUrl: './gaveta-nfs.component.css',
})
export class GavetaNfsComponent implements OnInit, OnDestroy {
  @Input() aberta = false;
  @Output() fechar = new EventEmitter<void>();
  /** Alimenta o contador do botão "Gaveta de NFs" no cabeçalho do board. */
  @Output() totalChange = new EventEmitter<number>();
  /** Concluiu o vínculo — o board recarrega o Kanban e a lista (mesmo tratamento de outras mutações). */
  @Output() vinculado = new EventEmitter<CompraVinculadaNFeStruct[]>();

  readonly urlFornecedor = environment.urlApiPessoa + 'Lookup/listPessoaSelect';
  readonly classeBadgeStatus = classeBadgeStatusNFeEntrada;
  readonly iconeStatus = iconeStatusNFeEntrada;
  readonly rotuloStatus = rotuloStatusNFeEntrada;

  readonly skeletonSlots = [1, 2, 3];

  notas: NotaFiscalEntradaGavetaStruct[] = [];
  carregando = false;
  /** A lista inteira está sendo trocada (abertura, atualizar, filtro). A paginação só acrescenta. */
  substituindo = false;
  totalRegistros = 0;
  ultimaPagina = true;
  filtroAberto = false;
  avisoOculto = false;
  /** Nota cujo contexto do vínculo está carregando (botão "Vincular ao pedido" em espera). */
  vinculandoId: number | null = null;

  readonly filtroModel = this.fb.group({
    fornecedor: [[] as number[]],
  });

  private paginaAtual = 0;
  private token = 0;
  private readonly fornecedores = new Map<number, FornecedorResumo>();
  /** Ids já perguntados à API.Pessoa — inclusive os que ela não devolveu. */
  private readonly fornecedoresConsultados = new Set<number>();
  /** Ids do lote em andamento na API.Pessoa (skeleton no nome do fornecedor). */
  private readonly fornecedoresCarregando = new Set<number>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.avisoOculto = localStorage.getItem(AVISO_OCULTO_STORAGE_KEY) === '1';
    this.filtroModel.controls.fornecedor.valueChanges
      .pipe(
        // O select de fornecedor é múltiplo: marcar várias opções emite um valueChanges por
        // clique. Debounce + distinctUntilChanged (mesmo estilo do componente pai) evitam uma
        // listagem completa por opção marcada.
        debounceTime(350),
        distinctUntilChanged((anterior, atual) => this.mesmosFornecedores(anterior, atual)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.recarregar());
    this.recarregar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get fornecedorControl(): FormControl {
    return this.filtroModel.controls.fornecedor as unknown as FormControl;
  }

  get temFiltro(): boolean {
    return !!this.filtroModel.controls.fornecedor.value?.length;
  }

  get vazia(): boolean {
    return !this.carregando && this.notas.length === 0;
  }

  /** Sem nenhuma nota pendente e sem filtro aplicado — estado vazio diferente do "sem resultado". */
  get vaziaSemCadastro(): boolean {
    return this.vazia && !this.temFiltro;
  }

  onFechar(): void {
    this.fechar.emit();
  }

  ocultarAviso(): void {
    this.avisoOculto = true;
    localStorage.setItem(AVISO_OCULTO_STORAGE_KEY, '1');
  }

  exibirAviso(): void {
    this.avisoOculto = false;
    localStorage.removeItem(AVISO_OCULTO_STORAGE_KEY);
  }

  alternarFiltro(): void {
    this.filtroAberto = !this.filtroAberto;
  }

  limparFiltro(): void {
    this.filtroModel.controls.fornecedor.setValue([]);
  }

  recarregar(): void {
    this.buscarPagina(0, true);
  }

  carregarMais(): void {
    if (this.carregando || this.ultimaPagina) return;
    this.buscarPagina(this.paginaAtual + 1, false);
  }

  /**
   * Nome do emitente do XML (o mesmo que a Central e a associação mostram); sem ele, o cadastro do fornecedor.
   * Enquanto o cadastro vem da API.Pessoa o card mostra o skeleton (`isFornecedorCarregando`).
   */
  nomeFornecedor(nota: NotaFiscalEntradaGavetaStruct): string {
    if (nota.nomeEmitente) return nota.nomeEmitente;
    if (!nota.idPessoaEmitente) return 'Fornecedor não identificado';
    return this.fornecedores.get(nota.idPessoaEmitente)?.nome ?? 'Fornecedor não identificado';
  }

  /** Nome do fornecedor ainda vindo da API.Pessoa (sem nome no XML e com o lote em andamento). */
  isFornecedorCarregando(nota: NotaFiscalEntradaGavetaStruct): boolean {
    return !nota.nomeEmitente && !!nota.idPessoaEmitente && this.fornecedoresCarregando.has(nota.idPessoaEmitente);
  }

  cnpjFornecedor(nota: NotaFiscalEntradaGavetaStruct): string | null {
    if (nota.cnpjEmitente) return nota.cnpjEmitente;
    if (!nota.idPessoaEmitente) return null;
    return this.fornecedores.get(nota.idPessoaEmitente)?.cnpj ?? null;
  }

  /**
   * Data exibida no card: a de emissão e, na falta dela, a de lançamento — que é o que a Central
   * chama de "Data Lançamento" (`dataEntradaSaida`). Nota lançada manualmente pode não ter emissão.
   */
  dataCard(nota: NotaFiscalEntradaGavetaStruct): Date | null {
    return nota.dataEmissao ?? nota.dataEntradaSaida ?? null;
  }

  rotuloDataCard(nota: NotaFiscalEntradaGavetaStruct): string {
    if (nota.dataEmissao) return 'Data de emissão';
    return nota.dataEntradaSaida ? 'Data de lançamento' : 'Sem data de emissão ou lançamento';
  }

  numeroNota(nota: NotaFiscalEntradaGavetaStruct): string {
    const numero = nota.numero ?? '—';
    return nota.serie ? `${numero}/${nota.serie}` : numero;
  }

  /**
   * Botão "Vincular ao pedido". O critério é "associações feitas", não o
   * status da nota: carrega o contexto do vínculo e, se nenhum item está em associação (`isItemAssociacaoPendente`),
   * abre o modal de vínculo com esse contexto; senão, mostra o aviso de associação
   * pendente. Enquanto o contexto carrega, o botão da nota fica em espera e os demais
   * "Vincular ao pedido" ficam bloqueados, para não abrir dois modais.
   */
  vincularAoPedido(nota: NotaFiscalEntradaGavetaStruct): void {
    if (this.vinculandoId !== null) return;
    this.vinculandoId = nota.idNFeEntrada;
    this.notaFiscalEntradaService
      .contextoVinculoPedido(nota.idNFeEntrada)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: contexto => {
          this.vinculandoId = null;
          if (contexto.isError) {
            this.alertService.show('Atenção', contexto.errorDescription, AlertType.warning);
            return;
          }
          const itens = contexto.itens ?? [];
          if (itens.length > 0 && !itens.some(isItemAssociacaoPendente)) {
            this.abrirVinculoPedidoNf(nota, contexto);
          } else {
            this.abrirAvisoAssociacaoPendente(nota);
          }
        },
        error: () => {
          this.vinculandoId = null;
          this.alertService.show('Erro', 'Não foi possível carregar os itens da NF-e.', AlertType.error);
        },
      });
  }

  /**
   * Botão "Resolver divergências" (secundário) - aparece na nota com divergência que impede o vínculo
   * com o pedido. Abre o cadastro da nota, o mesmo destino do "Tratar nota" da Central, e fecha a gaveta.
   */
  resolverDivergencia(nota: NotaFiscalEntradaGavetaStruct): void {
    if (this.vinculandoId !== null) return;
    this.onFechar();
    this.router.navigate(['/suprimentos/nota-fiscal-entrada/documento', nota.idNFeEntrada]);
  }

  private abrirAvisoAssociacaoPendente(nota: NotaFiscalEntradaGavetaStruct): void {
    const data: AvisoAssociacaoPendenteDialogData = { nota, nomeFornecedor: this.nomeFornecedor(nota) };
    this.dialog
      .open<AvisoAssociacaoPendenteDialogComponent, AvisoAssociacaoPendenteDialogData, AvisoAssociacaoPendenteDialogResult | undefined>(
        AvisoAssociacaoPendenteDialogComponent,
        {
          width: '468px',
          maxWidth: '92vw',
          autoFocus: false,
          data,
        },
      )
      .afterClosed()
      .subscribe(resultado => {
        if (resultado?.associar) this.abrirAssociacaoItens(nota);
      });
  }

  /**
   * Associação de itens da Central aberta pelo botão "Associar itens" (primário, só quando há item sem
   * produto) ou pelo aviso (origem Kanban/vínculo), com as mesmas opções de tela cheia da Central.
   * Recarrega a gaveta ao fechar — mesmo cancelado, o modal pode ter criado produto ou alterado NCM.
   * Não abre o vínculo em seguida: vincular é a ação "Vincular ao pedido" da gaveta.
   * Salva sem confirmar: avisa que a nota ainda não pode ser vinculada.
   */
  abrirAssociacaoItens(nota: NotaFiscalEntradaGavetaStruct): void {
    if (this.vinculandoId !== null) return;
    const data = new NotaFiscalEntradaAssociacaoDialogData();
    data.idNFeEntrada = nota.idNFeEntrada;
    data.origem = OrigemAssociacaoItensNFeEntradaEnum.KanbanVinculo;
    this.dialog
      .open<NotaFiscalEntradaAssociacaoDialogComponent, NotaFiscalEntradaAssociacaoDialogData, NotaFiscalEntradaAssociacaoDialogResult | undefined>(
        NotaFiscalEntradaAssociacaoDialogComponent,
        {
          width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh',
          disableClose: true, autoFocus: false, data, panelClass: 'nota-fiscal-entrada-associacao-dialog',
        },
      )
      .afterClosed()
      .subscribe(resultado => {
        // Recarrega em todos os casos: confirmada, a nota muda de status no card mesmo que o
        // vínculo seja cancelado depois.
        this.recarregar();
        // A associação termina aqui: o vínculo com o pedido é a ação "Vincular ao pedido" da gaveta.
        // Concluída com divergência bloqueante: o modal da associação já avisou o que falta.
        if (resultado?.salvo && !resultado.confirmado && !resultado.possuiDivergenciaBloqueante) {
          this.alertService.show(
            'Atenção',
            `NF-e ${this.numeroNota(nota)} continua Em lançamento na gaveta — a associação de itens não foi concluída, então ela ainda não pode ser vinculada a um pedido.`,
            AlertType.warning,
          );
        }
      });
  }

  /** Modal de vínculo, tela cheia — mesmo tratamento da associação da Central. */
  private abrirVinculoPedidoNf(
    nota: NotaFiscalEntradaGavetaStruct,
    contexto: NotaFiscalEntradaContextoVinculoPedidoResponse,
  ): void {
    const data: VinculoPedidoNfDialogData = {
      idNFeEntrada: nota.idNFeEntrada,
      nomeFornecedor: this.nomeFornecedor(nota),
      contexto,
    };
    const ref = this.dialog.open(VinculoPedidoNfDialogComponent, {
      width: '96vw',
      height: '92vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'vinculo-pedido-nf-dialog',
      disableClose: true,
      autoFocus: false,
      data,
    });
    ref.afterClosed().subscribe((result: VinculoPedidoNfDialogResult) => {
      if (!result?.vinculado) return;
      this.recarregar();
      this.vinculado.emit(result.compras);
    });
  }

  private buscarPagina(pagina: number, substituir: boolean): void {
    const token = ++this.token;
    this.carregando = true;
    this.substituindo = substituir;

    const request = new NotaFiscalEntradaGavetaListarRequest();
    request.listIdPessoaEmitente = this.filtroModel.controls.fornecedor.value?.length
      ? this.filtroModel.controls.fornecedor.value
      : null;
    request.pesquisar = null;
    request.dataDe = null;
    request.dataAte = null;

    this.notaFiscalEntradaService.listarGaveta(request, pagina).subscribe({
      next: (response) => {
        if (token !== this.token) return;
        this.carregando = false;
        this.substituindo = false;
        if (response.isError) {
          this.ultimaPagina = true;
          this.alertService.show('Gaveta de NFs', response.errorDescription, AlertType.error);
          return;
        }
        const recebidas = response.list ?? [];
        if (substituir) {
          this.notas = recebidas;
        } else {
          // Skip(N) é calculado pelo servidor com base na página: se alguém vincular uma nota
          // entre a busca da página 0 e da página 1, o conjunto desloca e uma nota já carregada
          // pode voltar aqui. Sem descartar a repetida, o @for quebra (track por idNFeEntrada
          // duplicado).
          const idsJaCarregados = new Set(this.notas.map(n => n.idNFeEntrada));
          const ineditas = recebidas.filter(n => !idsJaCarregados.has(n.idNFeEntrada));
          this.notas = [...this.notas, ...ineditas];
        }
        this.paginaAtual = pagina;
        this.totalRegistros = response.fullListSize ?? 0;
        // Baseado em this.notas.length (já deduplicado): se a página veio inteira de
        // repetidas, o total ainda não bateu e a busca continua na próxima página em vez de
        // parar cedo demais; como paginaAtual sempre avança, também não há risco de laço.
        this.ultimaPagina = this.notas.length >= this.totalRegistros || recebidas.length === 0;
        this.totalChange.emit(this.totalRegistros);
        this.carregarFornecedores(recebidas);
      },
      error: () => {
        if (token !== this.token) return;
        this.carregando = false;
        this.substituindo = false;
        this.ultimaPagina = true;
        this.alertService.show('Gaveta de NFs', 'Não foi possível carregar as notas da gaveta.', AlertType.error);
      },
    });
  }

  /** Nome e CNPJ do emitente vêm da API.Pessoa em lote — mesmo caminho da Central de NFs. */
  private carregarFornecedores(notas: NotaFiscalEntradaGavetaStruct[]): void {
    const ids = notas
      .map(x => x.idPessoaEmitente)
      .filter((id): id is number => !!id && !this.fornecedoresConsultados.has(id));
    if (!ids.length) return;

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [...new Set(ids)];
    request.listIdPessoa.forEach(id => {
      this.fornecedoresConsultados.add(id);
      this.fornecedoresCarregando.add(id);
    });
    // Falhou: avisa e solta os ids para uma próxima página ou o Atualizar tentarem de novo.
    const falhar = (mensagem: string) => {
      request.listIdPessoa.forEach(id => this.fornecedoresConsultados.delete(id));
      this.alertService.show('Erro', mensagem, AlertType.error);
    };
    this.pessoaLookupService
      .getListPessoaIds(request)
      .pipe(
        finalize(() => request.listIdPessoa.forEach(id => this.fornecedoresCarregando.delete(id))),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: response => {
          if (response.isError) {
            falhar(response.errorDescription || 'Não foi possível carregar os fornecedores das notas.');
            return;
          }
          response.listaPessoa?.forEach(pessoa =>
            this.fornecedores.set(pessoa.idPessoa, {
              nome: pessoa.nomeFantasia || pessoa.nome,
              cnpj: pessoa.cnpj ?? null,
            })
          );
        },
        error: () => falhar('Não foi possível carregar os fornecedores das notas.'),
      });
  }

  /**
   * Compara o conteúdo dos arrays do filtro de fornecedor, não a referência: o FormControl do
   * select múltiplo emite um array novo a cada opção marcada, mesmo quando o conjunto final de
   * ids é o mesmo (ex.: marcar e desmarcar rápido a mesma opção).
   */
  private mesmosFornecedores(a: number[] | null, b: number[] | null): boolean {
    const listaA = a ?? [];
    const listaB = b ?? [];
    return listaA.length === listaB.length && listaA.every((id, i) => id === listaB[i]);
  }
}
